import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import CertificateCard from '../../components/cultivar/CertificateCard';
import {
  Award, ChevronLeft, ChevronRight, Loader2, Filter, X
} from 'lucide-react';
import type { EntitlementCertificate, Pagination } from '../../types/cultivar';
import cultivarService from '../../services/cultivar';

function CertificateManagement() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [certificates, setCertificates] = useState<EntitlementCertificate[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const statusFilter = searchParams.get('status') || '';
  const currentPage = Number(searchParams.get('page')) || 1;

  const loadCertificates = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await cultivarService.getMyCertificates({
        status: statusFilter || undefined,
        page: currentPage,
        limit: 12,
      });

      if (response.success && response.data) {
        setCertificates(response.data.certificates);
        setPagination(response.data.pagination);
      } else {
        setError('Failed to load certificates');
      }
    } catch (err) {
      console.error('Error loading certificates:', err);
      setError('Failed to load certificates. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, currentPage]);

  useEffect(() => {
    loadCertificates();
  }, [loadCertificates]);

  const setFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.delete('page');
    setSearchParams(params, { replace: true });
  };

  const setPage = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(page));
    setSearchParams(params, { replace: true });
  };

  const statusOptions = [
    { value: '', label: 'All Certificates' },
    { value: 'active', label: 'Valid' },
    { value: 'expired', label: 'Expired' },
    { value: 'revoked', label: 'Revoked' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Entitlement Certificates"
        subtitle="View and download your cultivar entitlement certificates"
        icon={<Award className="h-6 w-6 text-white" />}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-200 mb-6 p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <Filter className="h-4 w-4 text-neutral-400" />
              <span className="text-sm font-medium text-neutral-700">Filter:</span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <select
                value={statusFilter}
                onChange={(e) => setFilter('status', e.target.value)}
                className="input-professional py-2 text-sm"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              {statusFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchParams({}, { replace: true })}
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        {!loading && certificates.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg border border-neutral-200 p-4 text-center">
              <p className="text-2xl font-bold text-neutral-900">{pagination?.total || 0}</p>
              <p className="text-sm text-neutral-500">Total Certificates</p>
            </div>
            <div className="bg-success-50 rounded-lg border border-success-200 p-4 text-center">
              <p className="text-2xl font-bold text-success-700">
                {certificates.filter(c => c.status === 'active').length}
              </p>
              <p className="text-sm text-success-600">Valid</p>
            </div>
            <div className="bg-warning-50 rounded-lg border border-warning-200 p-4 text-center">
              <p className="text-2xl font-bold text-warning-700">
                {certificates.filter(c => c.status === 'expired').length}
              </p>
              <p className="text-sm text-warning-600">Expired</p>
            </div>
            <div className="bg-error-50 rounded-lg border border-error-200 p-4 text-center">
              <p className="text-2xl font-bold text-error-700">
                {certificates.filter(c => c.status === 'revoked').length}
              </p>
              <p className="text-sm text-error-600">Revoked</p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-10 w-10 text-primary-600 animate-spin" />
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-error-600 mb-4">{error}</p>
              <Button variant="outline" onClick={loadCertificates}>
                Try Again
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {!loading && !error && certificates.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="h-16 w-16 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-4">
                <Award className="h-8 w-8 text-neutral-400" />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                No Certificates Found
              </h3>
              <p className="text-neutral-600 mb-6 max-w-md mx-auto">
                {statusFilter
                  ? 'No certificates match your current filter.'
                  : 'Certificates are generated when production declarations are acknowledged by the license holder.'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Certificate Grid */}
        {!loading && !error && certificates.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {certificates.map((certificate) => (
                <CertificateCard key={certificate.id} certificate={certificate} />
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setPage(currentPage - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>

                <span className="px-4 text-sm text-neutral-600">
                  Page {currentPage} of {pagination.pages}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === pagination.pages}
                  onClick={() => setPage(currentPage + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}

        {/* Info Panel */}
        <div className="mt-8 bg-primary-50 border border-primary-200 rounded-lg p-6">
          <h3 className="font-semibold text-primary-900 mb-3">
            About Entitlement Certificates
          </h3>
          <ul className="space-y-2 text-sm text-primary-800">
            <li className="flex items-start gap-2">
              <span className="text-primary-500 mt-0.5">•</span>
              <span>
                Certificates are automatically generated when a license holder acknowledges
                your production declaration.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-500 mt-0.5">•</span>
              <span>
                Each certificate includes a QR code that links to a public verification page,
                allowing auditors to confirm the certificate's validity.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-500 mt-0.5">•</span>
              <span>
                Download the PDF certificate and keep it with your compliance documentation
                for GlobalGAP, HARPS, or other audits.
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default CertificateManagement;
